import React from 'react';
import { RISK_LEVELS } from 'utils/constants';

interface RiskLevelTooltipContentProps {
  riskLevel?: string;
  className?: string; // Optional className for styling the bold text (e.g., styles.riskLevelText)
}

/**
 * Reusable component for displaying risk level in tooltips.
 * For "In Planning" risk level, displays only the text without "Risk Level:" prefix and without bold.
 * For other risk levels, displays "Risk Level: [displayName]" with bold styling.
 */
export const RiskLevelTooltipContent: React.FC<
  RiskLevelTooltipContentProps
> = ({ riskLevel, className }) => {
  // Helper to get display name for risk level
  const getRiskLevelDisplayName = (risk?: string): string => {
    if (!risk) return '';
    const normalized = risk.toLowerCase();
    if (normalized === 'green' || normalized === 'low') return 'Low';
    if (normalized === 'yellow' || normalized === 'moderate') return 'Moderate';
    if (normalized === 'red' || normalized === 'high') return 'High';
    if (normalized === 'in planning' || normalized === 'in_planning')
      return 'In Planning';
    return risk; // fallback to original value
  };

  const normalized = (riskLevel || '').toLowerCase();
  const isInPlanning =
    normalized === 'in planning' ||
    normalized === 'in_planning' ||
    riskLevel === RISK_LEVELS.IN_PLANNING;
  const displayName = getRiskLevelDisplayName(riskLevel);

  // For "In Planning", show without "Risk Level:" prefix and without bold
  if (isInPlanning) {
    return <>{displayName}</>;
  }

  // For other risk levels, show with "Risk Level:" prefix and bold styling
  return (
    <>
      Risk Level:{' '}
      {className ? (
        <span className={className}>{displayName}</span>
      ) : (
        <span style={{ fontWeight: 'bold' }}>{displayName}</span>
      )}
    </>
  );
};
